import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios"; // Import axios for API requests

// Styled container for dialog content
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

// Styled quote block
const QuoteBlock = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  height: "100%",
  boxShadow: theme.shadows[2],
  borderRadius: 4,
}));

// Contact form component
function ContactForm({ onClose }) {
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [responseMessage, setResponseMessage] = React.useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage(null);

    try {
      const response = await axios.post("/send-mail", formData);
      setResponseMessage({
        type: "success",
        text: "Message sent successfully!",
      });
      setFormData({ name: "", email: "", subject: "", message: "" }); // Clear form
    } catch (error) {
      setResponseMessage({
        type: "error",
        text: "Failed to send message. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label="Name"
        name="name"
        fullWidth
        margin="normal"
        variant="outlined"
        value={formData.name}
        onChange={handleChange}
        required
      />
      <TextField
        label="Email"
        name="email"
        type="email"
        fullWidth
        margin="normal"
        variant="outlined"
        value={formData.email}
        onChange={handleChange}
        required
      />
      <TextField
        label="Subject"
        name="subject"
        fullWidth
        margin="normal"
        variant="outlined"
        value={formData.subject}
        onChange={handleChange}
        required
      />
      <TextField
        label="Message"
        name="message"
        fullWidth
        margin="normal"
        variant="outlined"
        multiline
        rows={4}
        value={formData.message}
        onChange={handleChange}
        required
      />

      {responseMessage && (
        <Typography
          color={responseMessage.type === "success" ? "green" : "red"}
          sx={{ mt: 2 }}
        >
          {responseMessage.text}
        </Typography>
      )}

      <DialogActions sx={{ p: 0, mt: 2 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
        >
          {loading ? "Sending..." : "Send Message"}
        </Button>
      </DialogActions>
    </form>
  );
}

// Main Contact Us Dialog component
export default function ContactUsDialog({ open, onClose }) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          backgroundColor: theme.palette.primary.main,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography variant="h4" component="div" color="common.white">
            Contact Us
          </Typography>
        </Box>
      </DialogTitle>
      <StyledDialogContent>
        {/* Left side: Contact Form */}
        <Box sx={{ flex: 1, pr: { xs: 0, md: 2 } }}>
          <ContactForm onClose={onClose} />
        </Box>
        {/* Right side: Supportive block with quotes */}
        <QuoteBlock
          sx={{
            flex: 1,
            pl: { xs: 0, md: 2 },
            mt: { xs: 4, md: 0 },
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontStyle: "italic" }}>
            "Great customer service is not just a department, it's everyone's
            job."
          </Typography>
          <Typography variant="subtitle2" align="right">
            – Zenthra
          </Typography>
          <Box
            sx={{ mt: 4, borderTop: `1px solid ${theme.palette.divider}` }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            We are committed to providing prompt and thoughtful responses. Let
            us know how we can help you!
          </Typography>
        </QuoteBlock>
      </StyledDialogContent>
    </Dialog>
  );
}
